import { useMemo, useState } from "react";
import { Pressable, StyleSheet, TextInput, View } from "react-native";

import { Text } from "@/src/components/ui/BrandText";
import { SUBJECTS, TRACKS, type Subject, type Track } from "@/src/lib/taxonomy";
import { action, border, radius, spacing, surface, text } from "@/src/theme/tokens";
import type { SponsoredCampaignAdmin, SponsoredCampaignInput } from "./api";

function timestampIso(value: unknown) {
  if (value && typeof value === "object" && "toDate" in value && typeof value.toDate === "function") {
    return value.toDate().toISOString();
  }
  if (value && typeof value === "object" && "seconds" in value && typeof value.seconds === "number") {
    return new Date(value.seconds * 1000).toISOString();
  }
  return "";
}

export function campaignInputFromAdmin(campaign: SponsoredCampaignAdmin): SponsoredCampaignInput {
  return {
    advertiserName: campaign.advertiserName,
    headline: campaign.headline,
    body: campaign.body,
    mediaUrl: campaign.mediaUrl,
    ctaLabel: campaign.ctaLabel,
    ctaUrl: campaign.ctaUrl,
    targetSubjects: campaign.targetSubjects,
    targetTracks: campaign.targetTracks,
    startsAt: timestampIso(campaign.startsAt),
    endsAt: timestampIso(campaign.endsAt) || undefined,
    frequencyCap: campaign.frequencyCap,
  };
}

export function MonetizationCampaignForm({
  initial,
  submitLabel,
  busy,
  onSubmit,
}: {
  initial?: SponsoredCampaignInput;
  submitLabel: string;
  busy?: boolean;
  onSubmit: (value: SponsoredCampaignInput) => void | Promise<void>;
}) {
  const defaults = useMemo<SponsoredCampaignInput>(() => initial || ({
    advertiserName: "",
    headline: "",
    body: "",
    mediaUrl: "",
    ctaLabel: "",
    ctaUrl: "",
    targetSubjects: [],
    targetTracks: [],
    startsAt: new Date().toISOString(),
    endsAt: "",
    frequencyCap: { maxImpressions: 3, windowHours: 24 },
  }), [initial]);
  const [value, setValue] = useState(defaults);
  const [capImpressions, setCapImpressions] = useState(String(defaults.frequencyCap.maxImpressions));
  const [capHours, setCapHours] = useState(String(defaults.frequencyCap.windowHours));

  function toggleSubject(subject: Subject) {
    setValue((current) => ({
      ...current,
      targetSubjects: current.targetSubjects?.includes(subject)
        ? current.targetSubjects.filter((item) => item !== subject)
        : [...(current.targetSubjects || []), subject],
    }));
  }

  function toggleTrack(track: Track) {
    setValue((current) => ({
      ...current,
      targetTracks: current.targetTracks?.includes(track)
        ? current.targetTracks.filter((item) => item !== track)
        : [...(current.targetTracks || []), track],
    }));
  }

  function submit() {
    if (busy) return;
    void onSubmit({
      ...value,
      advertiserName: value.advertiserName.trim(),
      headline: value.headline?.trim() || undefined,
      body: value.body.trim(),
      mediaUrl: value.mediaUrl?.trim() || undefined,
      ctaLabel: value.ctaLabel?.trim() || undefined,
      ctaUrl: value.ctaUrl?.trim() || undefined,
      startsAt: value.startsAt.trim(),
      endsAt: value.endsAt?.trim() || undefined,
      frequencyCap: {
        maxImpressions: Number(capImpressions),
        windowHours: Number(capHours),
      },
    });
  }

  return (
    <View style={styles.form}>
      <View style={styles.group}>
        <Text style={styles.sectionTitle}>Contenu</Text>
        <Field label="Annonceur" value={value.advertiserName} onChangeText={(advertiserName) => setValue((current) => ({ ...current, advertiserName }))} />
        <Field label="Titre" value={value.headline || ""} onChangeText={(headline) => setValue((current) => ({ ...current, headline }))} />
        <Field multiline label="Texte sponsorisé" value={value.body} onChangeText={(body) => setValue((current) => ({ ...current, body }))} />
        <Field label="URL du média" helper="Lien HTTPS facultatif" value={value.mediaUrl || ""} onChangeText={(mediaUrl) => setValue((current) => ({ ...current, mediaUrl }))} />
        <View style={styles.row}>
          <View style={styles.grow}><Field label="Libellé du bouton" value={value.ctaLabel || ""} onChangeText={(ctaLabel) => setValue((current) => ({ ...current, ctaLabel }))} /></View>
          <View style={styles.grow}><Field label="Lien du bouton" value={value.ctaUrl || ""} onChangeText={(ctaUrl) => setValue((current) => ({ ...current, ctaUrl }))} /></View>
        </View>
      </View>
      <View style={styles.group}>
        <Text style={styles.sectionTitle}>Diffusion</Text>
        <Text style={styles.helper}>Définis la période pendant laquelle la campagne peut être diffusée.</Text>
        <View style={styles.row}>
          <View style={styles.grow}><Field helper="Date et heure complètes" label="Début de diffusion" value={value.startsAt} onChangeText={(startsAt) => setValue((current) => ({ ...current, startsAt }))} /></View>
          <View style={styles.grow}><Field helper="Laisse vide si la campagne n’a pas de fin" label="Fin de diffusion" value={value.endsAt || ""} onChangeText={(endsAt) => setValue((current) => ({ ...current, endsAt }))} /></View>
        </View>
      </View>
      <View style={styles.group}>
        <Text style={styles.sectionTitle}>Ciblage</Text>
        <Text style={styles.label}>Matières ciblées</Text>
        <Text style={styles.helper}>Aucune sélection = toutes les matières.</Text>
        <View style={styles.pills}>{SUBJECTS.map((subject) => <Pill key={subject.value} active={value.targetSubjects?.includes(subject.value)} label={subject.label} onPress={() => toggleSubject(subject.value)} />)}</View>
        <Text style={styles.label}>Filières ciblées</Text>
        <Text style={styles.helper}>Aucune sélection = toutes les filières.</Text>
        <View style={styles.pills}>{TRACKS.map((track) => <Pill key={track.value} active={value.targetTracks?.includes(track.value)} label={track.label} onPress={() => toggleTrack(track.value)} />)}</View>
      </View>
      <View style={styles.group}>
        <Text style={styles.sectionTitle}>Fréquence de diffusion</Text>
        <Text style={styles.helper}>Limite le nombre d’impressions par personne sur une période donnée.</Text>
        <View style={styles.row}>
          <View style={styles.grow}><Field keyboardType="number-pad" label="Impressions maximum" value={capImpressions} onChangeText={setCapImpressions} /></View>
          <View style={styles.grow}><Field keyboardType="number-pad" label="Période (heures)" value={capHours} onChangeText={setCapHours} /></View>
        </View>
      </View>
      <Pressable accessibilityRole="button" disabled={busy} onPress={submit} style={[styles.submit, busy && styles.disabled]}>
        <Text style={styles.submitText}>{busy ? "Enregistrement…" : submitLabel}</Text>
      </Pressable>
    </View>
  );
}

function Field({ label, helper, ...props }: { label: string; helper?: string } & React.ComponentProps<typeof TextInput>) {
  return <View style={styles.field}><Text style={styles.label}>{label}</Text><TextInput placeholderTextColor={text.muted} style={[styles.input, props.multiline && styles.multiline]} {...props} />{helper ? <Text style={styles.helper}>{helper}</Text> : null}</View>;
}

function Pill({ active, label, onPress }: { active?: boolean; label: string; onPress: () => void }) {
  return <Pressable accessibilityRole="checkbox" accessibilityState={{ checked: Boolean(active) }} onPress={onPress} style={[styles.pill, active && styles.pillActive]}><Text style={[styles.pillText, active && styles.pillTextActive]}>{label}</Text></Pressable>;
}

const styles = StyleSheet.create({
  form: { gap: spacing.xl },
  group: { gap: spacing.md, minWidth: 0 },
  sectionTitle: { color: text.primary, fontSize: 16, fontWeight: "800" },
  field: { gap: spacing.xs },
  label: { color: text.secondary, fontSize: 12, fontWeight: "700" },
  helper: { color: text.muted, fontSize: 12, lineHeight: 18 },
  input: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, color: text.primary, minHeight: 44, paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  multiline: { minHeight: 120, textAlignVertical: "top" },
  row: { flexDirection: "row", flexWrap: "wrap", gap: spacing.md },
  grow: { flex: 1, minWidth: 220 },
  pills: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
  pill: { borderColor: border.subtle, borderRadius: radius.pill, borderWidth: 1, paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  pillActive: { backgroundColor: surface.selected, borderColor: border.brand },
  pillText: { color: text.secondary, fontSize: 12, fontWeight: "600" },
  pillTextActive: { color: text.brand },
  submit: { alignItems: "center", alignSelf: "stretch", backgroundColor: action.primary, borderRadius: radius.md, minHeight: 48, justifyContent: "center", paddingHorizontal: spacing.xl, paddingVertical: spacing.md },
  submitText: { color: text.inverse, fontWeight: "800" },
  disabled: { opacity: 0.55 },
});
